import React, { useState } from 'react';
import { InspectionTypeOption } from './CalendarFilterPanel';

interface Props {
  types: InspectionTypeOption[];
  selected: number[];
  onChange: (ids: number[]) => void;
}

/**
 * SW2-2325 — the inspection-type filter.
 *
 * A *filter*, not a grouping, which is why it sits beside the group list rather than
 * as an entry in the "Group by" select. SW2-1856 specifies it that way, and grouping
 * projects by it would not hold together: a project carries many types over its life,
 * so it would belong to several groups at once, and picking the "Post Storm" group
 * would select those projects and then show all their routine inspections too.
 *
 * Split out of `CalendarFilterPanel` on review — it is a self-contained axis with its
 * own open/closed state and nothing else in the panel reads it.
 */
const CalendarInspectionTypeFilter: React.FC<Props> = ({
  types,
  selected,
  onChange,
}) => {
  const [open, setOpen] = useState(false);

  if (!types.length) return null;

  const toggle = (id: number) => {
    const next = new Set(selected);

    if (next.has(id)) next.delete(id);
    else next.add(id);

    onChange(Array.from(next));
  };

  return (
    <div className='calendar-filter__types'>
      <button
        type='button'
        className='calendar-filter__types-toggle'
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>
          Inspection type
          {selected.length > 0 && ` (${selected.length})`}
        </span>
        <span className='calendar-filter__caret'>{open ? '\u25B2' : '\u25BC'}</span>
      </button>

      {open && (
        <ul className='calendar-filter__type-list'>
          {types.map((type) => (
            <li key={type.id}>
              <label>
                <input
                  type='checkbox'
                  checked={selected.includes(type.id)}
                  onChange={() => toggle(type.id)}
                />
                <span className='calendar-filter__type-name'>{type.name}</span>
                <span className='calendar-filter__group-count'>
                  {type.projectCount}
                </span>
              </label>
            </li>
          ))}
          {selected.length > 0 && (
            <li>
              <button
                type='button'
                className='calendar-filter__more'
                onClick={() => onChange([])}
              >
                All inspection types
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default CalendarInspectionTypeFilter;
